import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import './NiftyRealty.css';
import RefreshButton from './RefreshButton';
import WatchlistStar from './WatchlistStar';

const NiftyRealty = ({ onBack }) => {
  const [universe, setUniverse] = useState('ALL'); // ALL or FO
  const [category, setCategory] = useState('10 DAY BREAKOUT');
  const [foSymbols, setFoSymbols] = useState([]);
  const [allSymbols, setAllSymbols] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSymbols, setLoadingSymbols] = useState(true);
  const [error, setError] = useState(null);

  // Attempt to load symbol universes dynamically; fall back if unavailable
  useEffect(() => {
    let cancelled = false;

    const loadUniverses = async () => {
      setLoadingSymbols(true);
      setError(null);
      try {
        // Fetch FO stocks from the backend
        const foRes = await fetch(`${API_BASE_URL}/fo-stocks`);
        let foList = [];
        if (foRes.ok) {
          const foJson = await foRes.json();
          foList = Array.isArray(foJson) ? foJson : [];
        } else {
          throw new Error('Failed to fetch F&O stocks');
        }

        // For  , we'll use the same list as FO stocks for now
        // If you have a separate endpoint for  , replace the URL accordingly
        const allList = [...foList];

        if (!cancelled) {
          setFoSymbols(foList);
          setAllSymbols(allList);
        }
      } catch (e) {
        console.error('Error loading stock symbols:', e);
        if (!cancelled) {
          setError('Failed to load stock symbols. Please try again later.');
        }
      } finally {
        if (!cancelled) setLoadingSymbols(false);
      }
    };

    loadUniverses();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedSymbols = useMemo(() => {
    const base = universe === 'FO' ? foSymbols : allSymbols;
    // To manage server load, sample a subset; adjust as needed or paginate
    return base.slice(0, 50);
  }, [universe, foSymbols, allSymbols]);

  const fetchData = useCallback(async () => {
    if (loadingSymbols) return;
      try {
        setLoading(true);
        
        if (category === '10 DAY BREAKOUT' || category === '50 DAY BREAKOUT') {
          // Convert selectedSymbols to the format expected by the backend
          const symbols = selectedSymbols.map(symbol => ({
            symbol: symbol,
            exchange: 'NSE'  // Assuming NSE as default exchange
          }));
          
          const response = await fetch(`${API_BASE_URL}/breakout-tenor-fifty`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              category: category === '10 DAY BREAKOUT' ? '10 DAY' : '50 DAY',
              symbols: symbols,
            }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const result = await response.json();
          // Extract data array from the response object
          const stocks = result.data && Array.isArray(result.data) ? result.data : [];
          setStocks(stocks);
        } else if (category === 'LONG TERM CHANNEL BREAKOUT') {
          const response = await fetch(`${API_BASE_URL}/channel-breakout`);
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const result = await response.json();
          // Check if we got a successful response with data array
          if (result.status === 'success' && Array.isArray(result.data)) {
            // Map the backend data to match frontend expectations
            const formattedData = result.data.map(item => ({
              symbol: item.stock || 'N/A',
              date: item.breakout_time || 'N/A',
              breakout_percentage: item.price_change_pct || 0,
              close: item.current_price || 0,
              volume: item.volume || 0,
              breakout_confirmed: item.breakout_confirmed || false,
              // Include all other fields from the backend
              ...item
            }));
            setStocks(formattedData);
          } else {
            setStocks([]);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
  }, [category, selectedSymbols, loadingSymbols]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="nifty-realty-page">
      {/* Header */}
      <header className="nifty-header">
        <div className="nifty-header-container">
          <div className="logo">
            <img src="images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U" />
          </div>
          
          <div className="nifty-user">
            <RefreshButton 
              onRefresh={fetchData} 
              isLoading={loading}
              size="small"
              style={{ marginRight: '10px' }}
            />
            <button className="nifty-back-btn" onClick={onBack}>← Back to Dashboard</button>
          </div>
        </div>
      </header>

      {/* Main Section */}
      <main className="nifty-main">
        <div className="nifty-container">
          <div className="nifty-title">
            <h1>Swing Zone <span className="info-icon">ℹ️</span></h1>
            <p className="warning">In Swing zone data will be shown for 10 days</p>

            <h2 className="category-heading">Categories</h2>
            <div className="breakout-buttons">
              <button
                className={category === '10 DAY BREAKOUT' ? 'breakout-btn active' : 'breakout-btn'}
                onClick={() => setCategory('10 DAY BREAKOUT')}
              >
                10 DAY BREAKOUT
                <p className="subtext">How to use ?</p>
              </button>
              <button
                className={category === '50 DAY BREAKOUT' ? 'breakout-btn active' : 'breakout-btn'}
                onClick={() => setCategory('50 DAY BREAKOUT')}
              >
                50 DAY BREAKOUT
                <p className="subtext">How to use ?</p>
              </button>
              <button
                className={category === 'LONG TERM CHANNEL BREAKOUT' ? 'breakout-btn active' : 'breakout-btn'}
                onClick={() => setCategory('LONG TERM CHANNEL BREAKOUT')}
              >
                CHANNEL BREAKOUT
                <p className="subtext">How to use ?</p>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loader-container">
              <div className="loader"></div>
              <p>Loading {category.toLowerCase()} data...</p>
            </div>
          ) : error ? (
            <div className="error-message">
              <p>{error}</p>
              <button onClick={() => window.location.reload()} className="retry-button">
                Retry
              </button>
            </div>
          ) : (
            <div className="nifty-table">
              {/* Table Head */}
              <div className="table-head--channelbreakout">
                <span>Name</span>
                <span>Date</span>
                <span>Percentage</span>
                <span>Close</span>
                {category === 'LONG TERM CHANNEL BREAKOUT' && <span>Volume</span>}
              </div>

              {/* Table Body */}
              <div className="table-body">
                {stocks.length > 0 ? (
                  stocks.map((stock, index) => (
                    <div key={index} className="table-row--10days">
                      <span className="stock-name">
                        <WatchlistStar 
                          stock={{
                            symbol: stock.symbol || stock.tradingsymbol || stock.name,
                            name: stock.symbol || stock.tradingsymbol || stock.name,
                            date: stock.date || stock.timestamp,
                            percentage: stock.percentage || stock.breakout_percentage,
                            close: stock.close || stock.last_price
                          }} 
                          size="16px"
                        />
                        {stock.symbol || stock.tradingsymbol || stock.name || 'N/A'}
                      </span>
                      <span>{stock.date || stock.timestamp || 'N/A'}</span>
                      <span className={(stock.percentage || stock.breakout_percentage || 0) >= 0 ? 'positive' : 'negative'}>
                        {stock.percentage !== undefined || stock.breakout_percentage !== undefined 
                          ? `${Number(stock.breakout_percentage || stock.percentage).toFixed(2)}%` 
                          : 'N/A'}
                      </span>
                      <span>₹{
                        stock.close || stock.last_price 
                          ? Number(stock.close || stock.last_price).toLocaleString('en-IN', {maximumFractionDigits: 2}) 
                          : 'N/A'
                      }</span>
                      {(category === 'LONG TERM CHANNEL BREAKOUT' || stock.volume || stock.traded_volume) && (
                        <span>{
                          stock.volume || stock.traded_volume 
                            ? Number(stock.volume || stock.traded_volume).toLocaleString('en-IN') 
                            : 'N/A'
                        }</span>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="no-data">
                    <p>No data available for {category.toLowerCase()}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default NiftyRealty;
